import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Users, Key, Settings, BarChart2, Compass, Network, FolderTree, Target, Calendar, Edit2, FileText, TrendingUp, Activity, BookOpen } from '../icons';
import { SidebarLayout } from './SidebarLayout';
import { RoleSwitcher } from '../RoleSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { isUserCoach } from '../../lib/auth';
import { supabase } from '../../lib/supabase';
import { LoadingSpinner } from '../LoadingSpinner';
import type { SidebarItem } from './Sidebar';

export function CoachLayout() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);
  const [hasCRMAccess, setHasCRMAccess] = useState(false);
  const [profileOnlyMode, setProfileOnlyMode] = useState(false);
  const [accountPlan, setAccountPlan] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/login');
      return;
    }

    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) {
        navigate('/');
        return;
      }

      const { data: coachData } = await supabase
        .from('coaches')
        .select('tier, teams_access_enabled, crm_enabled, profile_only_mode, account_plan, free_profile_onboarding_completed')
        .eq('user_id', user.id)
        .maybeSingle() as any;

      const isFreeProfileCoach = coachData?.account_plan === 'FREE_PROFILE_COACH';
      const onboardingDone = coachData?.free_profile_onboarding_completed === true;

      if (isFreeProfileCoach && !onboardingDone && window.location.pathname !== '/coach/onboarding') {
        navigate('/coach/onboarding', { replace: true });
        return;
      }

      setHasAIAccess(coachData?.tier === 'advanced' || coachData?.tier === 'partner');
      setHasTeamsAccess(coachData?.teams_access_enabled === true);
      setHasCRMAccess(coachData?.crm_enabled === true);
      setProfileOnlyMode(coachData?.profile_only_mode === true);
      setAccountPlan(coachData?.account_plan || null);
      setAuthorized(true);
      setChecking(false);
    };

    init();
  }, [user, authLoading, navigate]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Loading..." />;
  }

  const profileOnly = profileOnlyMode;
  const isFreeProfile = profileOnly && accountPlan === 'FREE_PROFILE_COACH';

  let items: SidebarItem[];

  if (isFreeProfile) {
    items = [
      { group: 'Profile', label: 'Dashboard', path: '/coach', icon: Brain },
      { group: 'Profile', label: 'Respondents', path: '/coach/respondents', icon: Users },
      { group: 'Profile', label: 'Assessment Codes', path: '/coach/codes', icon: Key },
      { group: 'Profile', label: 'Profile Resources', path: '/coach/profile-resources', icon: BookOpen },
      { group: 'Account', label: 'Upgrade', path: '/pricing', icon: TrendingUp },
    ];
  } else {
    items = [
      // Profile — always visible
      { group: 'Profile', label: 'Dashboard', path: '/coach', icon: Brain },
      { group: 'Profile', label: 'Respondents', path: '/coach/respondents', icon: Users },
      { group: 'Profile', label: 'Assessment Codes', path: '/coach/codes', icon: Key },
      { group: 'Profile', label: 'Projects', path: '/coach/projects', icon: FolderTree, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Profile', label: 'Teams', path: '/coach/teams', icon: Network, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Pattern
      { group: 'Pattern', label: 'AI Analysis', path: '/coach/manual-analysis', icon: Brain, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },

      // Progress
      { group: 'Progress', label: 'Client Progress', path: '/coach/progress', icon: Activity, gated: profileOnly, gateMessage: 'Not included in your plan' },
      { group: 'Progress', label: 'Transformation Reports', path: '/coach/transformation-reports', icon: FileText, gated: profileOnly, gateMessage: 'Not included in your plan' },

      // Pathways
      { group: 'Pathways', label: 'Transformation Pathways', path: '/coach/pathways', icon: Compass, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },
      { group: 'Pathways', label: 'Habit Installation OS\u2122', path: '/coach/habit-os', icon: Target, gated: profileOnly, gateMessage: 'Not included in your plan' },

      // Presence
      { group: 'Presence', label: 'Coaching Sessions', path: '/coach/sessions', icon: Calendar, gated: !hasCRMAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Presence', label: 'Session Intelligence\u2122', path: '/coach/session-intelligence', icon: Brain, gated: !hasCRMAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Proof
      { group: 'Proof', label: 'Team Reports', path: '/coach/reports', icon: BarChart2, gated: !hasTeamsAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Proof', label: 'Individual Reports', path: '/coach/respondents', icon: FileText, gated: profileOnly, gateMessage: 'Not included in your plan' },

      // Promote
      { group: 'Promote', label: 'Lead Page', path: '/coach/lead-page', icon: Edit2, gated: !hasCRMAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },
      { group: 'Promote', label: 'Sales Pipeline', path: '/coach/crm', icon: Target, gated: !hasCRMAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Contact your partner to enable' },

      // Profit
      { group: 'Profit', label: 'Profit Center', path: '/coach/profit', icon: TrendingUp, gated: profileOnly, gateMessage: 'Not included in your plan' },

      // Settings
      { group: 'Settings', label: 'AI Coach Settings', path: '/coach/ai-settings', icon: Settings, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },
      { group: 'Settings', label: 'AI Coach Usage', path: '/coach/ai-usage', icon: BarChart2, gated: !hasAIAccess || profileOnly, gateMessage: profileOnly ? 'Not included in your plan' : 'Requires Advanced tier' },
    ];
  }

  return (
    <SidebarLayout
      items={items}
      title="Coach"
      subtitle="Manage respondents & insights"
      headerSlot={<RoleSwitcher currentRole="coach" />}
    />
  );
}
